/**
 * Zod schemas for runtime validation of server responses. The server is trusted
 * not to leak plaintext, but parsing every response still catches contract drift
 * and shields the portal from a malformed/old API. Schemas mirror the types in
 * `@heivol-locker/shared-types`; keep them in lockstep.
 */
import { z } from 'zod';
const iso = z.string();
const b64 = z.string();
const uuid = z.string();
export const zArgon2idParams = z.object({
    m: z.number(),
    t: z.number(),
    p: z.number(),
    outputLen: z.literal(32),
});
export const zEnvelopeWire = z.object({
    id: uuid,
    kind: z.enum(['passphrase', 'recovery', 'device', 'mitid']),
    deviceId: uuid.nullable(),
    blob: b64,
});
export const zMe = z.object({
    userId: uuid,
    heivolIdSub: z.string(),
    email: z.string(),
    enrolled: z.boolean(),
    plan: z.literal('free'),
    recoveryCodeAcknowledgedAt: iso.nullable(),
    lockedAt: iso.nullable(),
    createdAt: iso,
});
export const zDeviceRecord = z.object({
    id: uuid,
    name: z.string(),
    kind: z.enum(['chrome-ext', 'web-portal']),
    publicKey: b64,
    enrolledAt: iso,
    lastSeenAt: iso.nullable(),
    revokedAt: iso.nullable(),
    thisDevice: z.boolean(),
});
export const zVaultRecord = z.object({
    id: uuid,
    nameBlob: b64,
    wrappedDek: b64,
    createdAt: iso,
    itemCount: z.number(),
});
export const zItemRecord = z.object({
    id: uuid,
    vaultId: uuid,
    kind: z.enum(['login', 'note', 'card', 'identity']),
    domainHash: b64.nullable(),
    wrappedDek: b64,
    ciphertext: b64,
    version: z.number(),
    updatedAt: iso,
    deletedAt: iso.nullable(),
});
export const zPairingRequestRecord = z.object({
    id: uuid,
    newDeviceName: z.string(),
    newDevicePublicKey: b64,
    token: z.string(),
    status: z.enum(['pending', 'approved', 'claimed', 'expired']),
    createdAt: iso,
    expiresAt: iso,
});
export const zAuditEntry = z.object({
    id: z.string(),
    event: z.string(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    actor: z.string(),
    reason: z.string().nullable(),
    at: iso,
});
// Response wrappers
export const zEnrollResponse = z.object({ me: zMe, device: zDeviceRecord });
export const zRecoveryAckResponse = z.object({ recoveryCodeAcknowledgedAt: iso });
export const zUnlockEnvelopesResponse = z.object({
    userId: uuid,
    passphraseEnvelope: zEnvelopeWire,
    argon2SaltPass: b64,
    params: zArgon2idParams,
});
export const zUnlockAttemptResponse = z.object({
    remainingAttempts: z.number(),
    lockedUntil: iso.nullable(),
});
export const zRecoveryStartResponse = z.object({
    userId: uuid,
    recoveryEnvelope: zEnvelopeWire,
    argon2SaltRec: b64,
    params: zArgon2idParams,
    cooldownUntil: iso,
});
export const zRecoveryCompleteResponse = z.object({ me: zMe });
export const zVaultResponse = z.object({ vault: zVaultRecord });
export const zVaultList = z.array(zVaultRecord);
export const zItemResponse = z.object({ item: zItemRecord });
export const zItemListResponse = z.object({
    items: z.array(zItemRecord),
    cursor: iso.nullable(),
});
export const zItemLookupResponse = z.object({ items: z.array(zItemRecord) });
export const zDeviceList = z.array(zDeviceRecord);
export const zPairInitResponse = z.object({ token: z.string(), expiresAt: iso });
export const zPairPendingResponse = z.object({ request: zPairingRequestRecord.nullable() });
export const zPairApproveResponse = z.object({ device: zDeviceRecord });
export const zPairClaimResponse = z.object({
    status: z.enum(['pending', 'approved', 'claimed', 'expired']),
    deviceEnvelope: zEnvelopeWire.nullable(),
});
export const zAuditListResponse = z.object({
    entries: z.array(zAuditEntry),
    cursor: iso.nullable(),
});
export const zWipeStartResponse = z.object({ ok: z.literal(true), expiresAt: iso });
export const zOk = z.object({ ok: z.literal(true) });
//# sourceMappingURL=schemas.js.map